<template>
  <Suspense>
    <component
      :is="modalComponent"
      @confirmNumber="handleCapturedEvent"
      v-bind="modalProps"
    ></component>
  </Suspense>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";

// modules
import ModalModule from "@/store/modules/modal";

// components
import PosConfiguration from "@/components/modal/PosConfiguration.vue";
import PCSelect from "@/components/modal/PCSelect.vue";
import Preferences from "@/components/modal/preferences/Preferences.vue";
import Products from "@/views/layout/components/footer/modal/Products.vue";
import Calculator from "@/components/modal/calculator/Calculator.vue";
import PaymentCalculator from "@/components/modal/payment-calculator/PaymentCalculator.vue";
import Settings from "@/components/modal/settings/Settings.vue";
import Analytics from "@/components/modal/Analytics.vue";
import Orders from "@/components/modal/orders/Orders.vue";
import Customers from "@/components/modal/customers/Customers.vue";
import CustomerCreate from "@/components/modal/customer-create/CustomerCreate.vue";
import CashierReports from "@/components/modal/cashier-reports/CashierReports.vue";
import FiscalReports from "@/components/modal/fiscal-reports/FiscalReports.vue";
import SendEmail from "../modal/SendEmail.vue";
import PaymentProgress from "@/components/modal/payment-progress/PaymentProgress.vue";
import EditCustomer from "@/components/modal/customers-edit/CustomerEdit.vue";
import DiscountSelectTickets from "@/components/modal/discount-select-tickets/DiscountSelectTickets.vue";
import AvailabilityInfo from "@/components/modal/quantity-not-available/AvailabilityInfo.vue";
import PrintTicketsAndReceipt from "@/components/modal/print-tickets-receipts/PrintTicketsReceipts.vue";
import DownloadTickets from "@/components/modal/DownloadTickets.vue";
import CustomFields from "@/components/modal/custom-fields/CustomFields.vue";
import NewCashFloatOpen from "@/components/modal/new-cashfloat-open/NewCashFloatOpen.vue";
import CashInOut from "@/components/modal/cash-in-out/CashInOut.vue";
import CashInOutV2 from "@/components/modal/cash-in-out/CashInOutV2.vue";
import { isCashbookV2FlowEnabled } from "@/utils/helpers/feature-flags";
import ReloadConfiguration from "@/components/modal/ReloadConfig.vue";
import OccupancyCalendar from "@/components/modal/occupancy-calendar/OccupancyCalendar.vue";
import ScanQrCode from "@/components/modal/scan-qr-code/ScanQrCode.vue";
import Addons from "@/components/modal/addons/Addons.vue";
import PromocodeCampaignsModal from "@/components/modal/promocode/PromocodeCampaignsModal.vue";
import PromocodeCampaignsTicketsModal from "@/components/modal/promocode/PromocodeCampaignsTickets.vue";
import TabNameEditModal from "@/components/modal/tab-name/TabName.vue";
import PromocodeProcessing from "@/components/modal/promocode/PromocodeProcessing.vue";

// interfaces
import { ModalComponents, ModalStatus } from "@/constants/modal";
import BookingModule from "@/store/modules/booking";
import { BookingStep } from "@/constants/booking";
import { PaymentTool } from "@/types/db/accounting";
import POSConfigModule from "@/store/modules/pos-config";
import { Currencies, Currency } from "@/constants/currency";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { roundToNearestMultipleOf5 } from "@/utils/helpers/common";

export default defineComponent({
  name: "Modal",
  components: {
    PosConfiguration,
    PCSelect,
    Preferences,
    Products,
    Calculator,
    PaymentCalculator,
    Settings,
    Analytics,
    Orders,
    Customers,
    CustomerCreate,
    CashierReports,
    FiscalReports,
    DiscountSelectTickets,
    AvailabilityInfo,
    SendEmail,
    PrintTicketsAndReceipt,
    EditCustomer,
    PaymentProgress,
    DownloadTickets,
    CustomFields,
    NewCashFloatOpen,
    CashInOut,
    CashInOutV2,
    ReloadConfiguration,
    OccupancyCalendar,
    ScanQrCode,
    Addons,
    PromocodeCampaignsModal,
    PromocodeCampaignsTicketsModal,
    TabNameEditModal,
    PromocodeProcessing,
  },
  setup() {
    const status = computed(() => {
      return ModalModule.status;
    });
    const modalProps = computed(() => {
      return { modalProps: ModalModule.modalProps };
    });
    const selectedBooking = computed(() => {
      const booking = BookingModule.selectedBooking;
      if (!booking) {
        throw new Error(PSBErrors.NoBookingInModule);
      }
      return booking;
    });

    function handleCapturedEvent(number: number, currency: Currency | null) {
      if (BookingModule.bookingStep === BookingStep.Payment) {
        if (number === 0) {
          // do not accept a payment of 0
          ModalModule.setStatus(ModalStatus.Hidden);
          return;
        }
        const paymentTool =
          BookingModule.bookingsAmountsTrack[BookingModule.selectedBookingUUID]
            .paymentTool;

        BookingModule.addCurrentAmount({
          BookingUUID: BookingModule.selectedBookingUUID,
          amount: number,
        });
        // If we're in a booking that support split payment then we set the splitPaymentAmount here,
        // this split amount will have accountings created based on it, it shouldn't be more than what is left to pay for the booking
        if (BookingModule.doesSupportSplitPayment) {
          let roundedNumber;
          if (currency) {
            number = number * (1 / currency.exchangeRate);

            // if currency is not CHF we keep roundedAmount = amount for simplicity
            roundedNumber = number;

            if (selectedBooking.value.currency === Currencies.CHF) {
              roundedNumber =
                roundToNearestMultipleOf5(Math.round(number * 100)) / 100;
            }
          }
          // Belgium: cash is only ever handed over in multiples of 5 cents, so
          // the entered amount is clamped rather than accepted as typed.
          const requiresRounding =
            POSConfigModule.requiresCashRounding &&
            paymentTool === PaymentTool.Cash;
          if (requiresRounding) {
            roundedNumber =
              roundToNearestMultipleOf5(Math.round(number * 100)) / 100;
          }
          BookingModule.setSplitPaymentAmount({
            BookingUUID: BookingModule.selectedBookingUUID,
            splitAmount: roundedNumber || number /*Math.min(
            amount,
            Math.abs(BookingModule.leftToPayAmount / 100)
          )*/,
            splitAmountProvided: requiresRounding ? roundedNumber : number,
          });
        }

        // Hide modal
        ModalModule.setStatus(ModalStatus.Hidden);
      }

      if (BookingModule.bookingStep === BookingStep.Discount) {
        // Here we update the discount value
        BookingModule.updateDiscountValue({
          BookingUUID: BookingModule.selectedBookingUUID,
          value: number,
        });
        ModalModule.setStatus(ModalStatus.Hidden);
      }
    }

    const modalComponent = computed(() => {
      if (status.value === ModalStatus.PosConfig) {
        return ModalComponents.POSConfig;
      }

      if (status.value === ModalStatus.PCSelect) {
        return ModalComponents.PCSelect;
      }

      if (status.value === ModalStatus.Preferences) {
        return ModalComponents.Preferences;
      }

      if (status.value === ModalStatus.Settings) {
        return ModalComponents.Settings;
      }

      if (status.value === ModalStatus.Calculator) {
        return ModalStatus.Calculator;
      }

      if (status.value === ModalStatus.PaymentCalculator) {
        return ModalStatus.PaymentCalculator;
      }

      if (status.value === ModalStatus.Orders) {
        return ModalStatus.Orders;
      }

      if (status.value === ModalStatus.Products) {
        return ModalStatus.Products;
      }

      if (status.value === ModalStatus.Customers) {
        return ModalStatus.Customers;
      }

      if (status.value === ModalStatus.Analytics) {
        return ModalStatus.Analytics;
      }

      if (status.value === ModalStatus.CustomerCreate) {
        return ModalStatus.CustomerCreate;
      }

      if (status.value === ModalStatus.CashierReports) {
        return ModalStatus.CashierReports;
      }

      if (status.value === ModalStatus.FiscalReports) {
        return ModalStatus.FiscalReports;
      }

      if (status.value === ModalStatus.DiscountSelectTickets) {
        return ModalStatus.DiscountSelectTickets;
      }

      if (status.value === ModalStatus.AvailabilityInfo) {
        return ModalStatus.AvailabilityInfo;
      }
      if (status.value === ModalStatus.SendEmail) {
        return ModalStatus.SendEmail;
      }
      if (status.value === ModalStatus.PrintTicketsAndReceipt) {
        return ModalStatus.PrintTicketsAndReceipt;
      }
      if (status.value === ModalStatus.PaymentProgress) {
        return ModalStatus.PaymentProgress;
      }
      if (status.value === ModalStatus.EditCustomer) {
        return ModalStatus.EditCustomer;
      }
      if (status.value === ModalStatus.DownloadTickets) {
        return ModalStatus.DownloadTickets;
      }
      if (status.value === ModalStatus.CustomFields) {
        return ModalStatus.CustomFields;
      }
      // if (status.value === ModalStatus.NewCashFloatOpen) {
      //   return ModalStatus.NewCashFloatOpen;
      // }
      if (status.value === ModalStatus.CashInOut) {
        return isCashbookV2FlowEnabled()
          ? ModalStatus.CashInOutV2
          : ModalStatus.CashInOut;
      }
      if (status.value === ModalStatus.ReloadConfiguration) {
        return ModalStatus.ReloadConfiguration;
      }

      if (status.value === ModalStatus.OccupancyCalendar) {
        return ModalComponents.OccupancyCalendar;
      }

      if (status.value === ModalStatus.ScanQrCode) {
        return ModalStatus.ScanQrCode;
      }

      if (status.value === ModalStatus.Addons) {
        return ModalStatus.Addons;
      }

      if (status.value === ModalStatus.PromocodeCampaigns) {
        return ModalStatus.PromocodeCampaigns;
      }

      if (status.value === ModalStatus.PromocodeCampaignsTickets) {
        return ModalStatus.PromocodeCampaignsTickets;
      }

      if (status.value === ModalStatus.TabNameEdit) {
        return ModalStatus.TabNameEdit;
      }

      if (status.value === ModalStatus.PromocodeProcessing) {
        return ModalStatus.PromocodeProcessing;
      }
      // By default, hidden
      return null;
    });

    return {
      modalComponent,
      handleCapturedEvent,
      modalProps,
    };
  },
});
</script>

<style scoped></style>
